import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

// iOS Safari never fires beforeinstallprompt — there's no programmatic
// install API at all there. The only path is "Share → Add to Home
// Screen", done manually by the user, so this needs its own instructional
// variant instead of silently showing nothing on iPhones.
const isIOS = () => /iphone|ipad|ipod/i.test(window.navigator.userAgent) && !window.MSStream;
const isStandalone = () => window.navigator.standalone === true || window.matchMedia('(display-mode: standalone)').matches;

const InstallPrompt = () => {
  const { pathname } = useLocation();
  const isPassenger = !pathname.startsWith('/admin');
  const dismissKey  = isPassenger ? 'pwa-passenger-dismissed' : 'pwa-admin-dismissed';

  const [prompt, setPrompt]       = useState(null);
  const [visible, setVisible]     = useState(false);
  const [iosVariant, setIosVariant] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (localStorage.getItem(dismissKey)) return;
    if (isStandalone()) return;

    // Android/Chrome/Edge — real install prompt via the native event.
    const handler = (e) => {
      e.preventDefault();
      setPrompt(e);
      setIosVariant(false);
      setVisible(true);
    };
    window.addEventListener('beforeinstallprompt', handler);

    // iOS Safari — no event to listen for; show instructions instead.
    if (isIOS()) {
      setIosVariant(true);
      setVisible(true);
    }

    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, [dismissKey]);

  if (!visible || dismissed) return null;

  const install = async () => {
    if (!prompt) return;
    prompt.prompt();
    const { outcome } = await prompt.userChoice;
    if (outcome === 'accepted') setVisible(false);
    setPrompt(null);
  };

  const dismiss = () => {
    setDismissed(true);
    localStorage.setItem(dismissKey, '1');
  };

  const title    = isPassenger ? 'ثبّت تطبيق سافر للحجز' : 'ثبّت تطبيق سافر';
  const subtitle = isPassenger ? 'احجز تذكرتك من أي مكان'  : 'وصول فوري من الشاشة الرئيسية';
  const features = isPassenger
    ? [['⚡', 'يفتح فورياً بدون متصفح'], ['🎫', 'تذاكرك دائماً في متناول يدك'], ['🔔', 'إشعارات رحلاتك تلقائياً']]
    : [['⚡', 'يفتح فورياً بدون متصفح'], ['📴', 'يعمل جزئياً بدون إنترنت'],    ['🔔', 'تحديثات تلقائية في الخلفية']];

  return (
    <div
      dir="rtl"
      className="fixed bottom-4 left-4 right-4 z-50 md:left-auto md:right-6 md:w-80"
      style={{ animation: 'slideUp 0.35s ease' }}
    >
      <style>{`@keyframes slideUp { from { transform: translateY(100px); opacity:0 } to { transform: translateY(0); opacity:1 } }`}</style>
      <div className="bg-white rounded-2xl shadow-2xl border border-blue-100 overflow-hidden">
        <div className="bg-blue-700 px-4 py-3 flex items-center gap-3">
          <span className="text-2xl">🚌</span>
          <div>
            <p className="text-white font-bold text-sm leading-tight">{title}</p>
            <p className="text-blue-200 text-xs">{subtitle}</p>
          </div>
          <button onClick={dismiss} className="mr-auto text-blue-200 hover:text-white text-xl leading-none">✕</button>
        </div>

        {iosVariant ? (
          <>
            {/* iOS has no install button to trigger — walk the user through
                the manual Share-sheet steps instead. */}
            <div className="px-4 py-3 space-y-2.5 text-sm text-gray-700">
              <div className="flex items-center gap-2.5">
                <span className="w-6 h-6 rounded-full bg-blue-50 text-blue-700 font-bold text-xs flex items-center justify-center flex-shrink-0">١</span>
                <span className="flex items-center gap-1.5">
                  اضغط زر المشاركة
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="text-blue-700">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v13m0-13l4 4m-4-4L8 7M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7"/>
                  </svg>
                  في المتصفح
                </span>
              </div>
              <div className="flex items-center gap-2.5">
                <span className="w-6 h-6 rounded-full bg-blue-50 text-blue-700 font-bold text-xs flex items-center justify-center flex-shrink-0">٢</span>
                <span>اختر "إضافة إلى الشاشة الرئيسية"</span>
              </div>
            </div>
            <div className="px-4 pb-4">
              <button onClick={dismiss}
                className="w-full bg-gray-100 hover:bg-gray-200 text-gray-600 font-medium py-2.5 rounded-xl text-sm transition">
                فهمت
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="px-4 py-3 space-y-2">
              {features.map(([icon, text]) => (
                <div key={text} className="flex items-center gap-2 text-sm text-gray-700">
                  <span>{icon}</span><span>{text}</span>
                </div>
              ))}
            </div>

            <div className="px-4 pb-4 flex gap-2">
              <button onClick={install}
                className="flex-1 bg-blue-700 hover:bg-blue-800 text-white font-bold py-2.5 rounded-xl text-sm transition">
                تثبيت الآن
              </button>
              <button onClick={dismiss}
                className="px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-600 font-medium rounded-xl text-sm transition">
                لاحقاً
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default InstallPrompt;
